import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [roles, setRoles] = useState(['RIDER']);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');

  const toggleRole = (role) => {
    setRoles(prev => {
      if (prev.includes(role)) {
        const next = prev.filter(r => r !== role);
        return next.length > 0 ? next : prev; // Keep at least one
      }
      return [...prev, role];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await register(email, password, name, roles);
    if (result.success) {
      toast.success('Account created successfully!');
      navigate(returnUrl || '/dashboard');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_2decf1d9-6688-4b47-98c1-1893beae897a/artifacts/lr28yf8y_43425DE4-4AE4-4784-89F6-4AA746534747.png"
            alt="Show Master Logo"
            className="h-[120px] w-[120px] mx-auto mb-4"
          />
          <h1 className="text-3xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}}>Create Account</h1>
          <p className="text-[#9E9E9E]">Join the platform</p>
        </div>

        <div className="bg-[#161616] border border-[#262626] rounded-lg p-8">
          <form onSubmit={handleSubmit} data-testid="register-form" className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Name</label>
              <input
                data-testid="register-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                placeholder="Your name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Email</label>
              <input
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Password</label>
              <input
                data-testid="register-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                placeholder="Create a password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-3">I am a</label>
              <div className="space-y-2">
                <button
                  type="button"
                  data-testid="role-rider-btn"
                  onClick={() => toggleRole('RIDER')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all text-left ${
                    roles.includes('RIDER')
                      ? 'border-[#0096FF] bg-[#0096FF]/10'
                      : 'border-[#262626] hover:border-[#0096FF]/40'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    roles.includes('RIDER') ? 'border-[#0096FF] bg-[#0096FF]' : 'border-[#525252]'
                  }`}>
                    {roles.includes('RIDER') && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Rider / Contestant</p>
                    <p className="text-[#9E9E9E] text-xs">Enter shows, track results, earn points</p>
                  </div>
                </button>

                <button
                  type="button"
                  data-testid="role-producer-btn"
                  onClick={() => toggleRole('ORG_ADMIN')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all text-left ${
                    roles.includes('ORG_ADMIN')
                      ? 'border-[#00D4FF] bg-[#00D4FF]/10'
                      : 'border-[#262626] hover:border-[#00D4FF]/40'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    roles.includes('ORG_ADMIN') ? 'border-[#00D4FF] bg-[#00D4FF]' : 'border-[#525252]'
                  }`}>
                    {roles.includes('ORG_ADMIN') && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Show Producer / Organization</p>
                    <p className="text-[#9E9E9E] text-xs">Create & manage shows, scoring, payouts</p>
                  </div>
                </button>
              </div>
              {roles.length === 2 && (
                <p className="text-[#0096FF] text-xs mt-2 font-medium">Both selected — you can produce shows AND enter as a rider</p>
              )}
            </div>

            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#9E9E9E]">
              Already have an account?{' '}
              <Link to={`/login${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`} className="text-[#0096FF] hover:text-[#00D4FF] font-medium">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
